'use client'

import { useTranslation } from '@/hooks/client/use-translation'

import { MobilePriceConfigurationSelect } from './mobile-price-configuration-select'

interface PriceLevelOption {
  id: number
  name: string
  sortOrder: number
  lowestPrice: string | null
}

interface PriceModelOption {
  id: number
  name: string
  levels: PriceLevelOption[]
}

interface PriceConfigurationSelectorProps {
  models: PriceModelOption[]
  selectedModelId: number
  selectedLevelId: number
  showModelSelector: boolean
  onModelChange: (modelId: number) => void
  onLevelChange: (levelId: number) => void
  onVariantChange: (modelId: number, levelId: number) => void
}

export const PriceConfigurationSelector = ({
  models,
  selectedModelId,
  selectedLevelId,
  showModelSelector,
  onModelChange,
  onLevelChange,
  onVariantChange,
}: PriceConfigurationSelectorProps) => {
  const { t } = useTranslation()
  const orderedModels = models.map((model) => ({
    ...model,
    levels: [...model.levels].sort((a, b) => a.sortOrder - b.sortOrder),
  }))
  const selectedModel = orderedModels.find((model) => model.id === selectedModelId) ?? orderedModels[0]
  const selectableModels = showModelSelector || !selectedModel ? orderedModels : [selectedModel]
  const variantCount = selectableModels.reduce((count, model) => count + model.levels.length, 0)

  if (!selectedModel || variantCount < 2) {
    return null
  }

  return (
    <div className="mt-3">
      <div className="sm:hidden">
        <p className="text-navy-60 mb-2 text-xs font-semibold tracking-wide uppercase">
          {showModelSelector
            ? t('product.chooseHearingAid', 'Choose a hearing aid')
            : t('product.chooseTechnologyLevel', 'Choose technology level')}
        </p>
        <MobilePriceConfigurationSelect
          models={selectableModels}
          selectedModelId={selectedModelId}
          selectedLevelId={selectedLevelId}
          ariaLabel={
            showModelSelector
              ? t('client.product.selectHearingAid', 'Select hearing aid')
              : t('client.product.selectTechnologyLevel', 'Select technology level')
          }
          onValueChange={onVariantChange}
        />
      </div>

      <div className="hidden flex-wrap items-end gap-x-7 gap-y-4 sm:flex">
        {showModelSelector && (
          <div className="w-fit max-w-full shrink-0">
            <p className="text-navy-60 mb-2 text-xs font-semibold tracking-wide uppercase">
              {t('product.chooseModel', 'Choose a model')}
            </p>
            <div
              className="flex max-w-full items-center gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1"
              role="tablist"
              aria-label={t('client.product.selectModel', 'Select model')}
            >
              {orderedModels.map((model) => {
                const isSelected = model.id === selectedModelId
                return (
                  <button
                    key={model.id}
                    type="button"
                    role="tab"
                    aria-selected={isSelected}
                    onClick={() => onModelChange(model.id)}
                    data-tracking="ignore"
                    className={`cursor-pointer rounded-lg px-3.5 py-2 text-sm font-medium whitespace-nowrap transition-all ${
                      isSelected
                        ? 'text-navy bg-white shadow-sm ring-1 ring-black/[0.04]'
                        : 'text-navy-60 hover:text-navy hover:bg-white/50'
                    }`}
                  >
                    {model.name}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {selectedModel.levels.length > 1 && (
          <div className="w-fit max-w-full shrink-0">
            <p className="text-navy-60 mb-2 text-xs font-semibold tracking-wide uppercase">
              {t('product.chooseTechnologyLevel', 'Choose technology level')}
            </p>
            <div
              className="flex max-w-full items-center gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1"
              role="tablist"
              aria-label={t('client.product.selectTechnologyLevel', 'Select technology level')}
            >
              {selectedModel.levels.map((level) => {
                const isSelected = level.id === selectedLevelId
                return (
                  <button
                    key={level.id}
                    type="button"
                    role="tab"
                    aria-selected={isSelected}
                    onClick={() => onLevelChange(level.id)}
                    data-tracking="ignore"
                    className={`cursor-pointer rounded-lg px-3.5 py-2 text-sm font-medium transition-all ${
                      isSelected
                        ? 'text-navy bg-white shadow-sm ring-1 ring-black/[0.04]'
                        : 'text-navy-60 hover:text-navy hover:bg-white/50'
                    }`}
                  >
                    <span className="whitespace-nowrap">{level.name}</span>
                    {level.lowestPrice && (
                      <span className={`ml-1.5 text-xs ${isSelected ? 'text-navy-60' : 'text-navy-40'}`}>
                        {level.lowestPrice}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
